import { useLanguage } from "@/i18n/LanguageContext";
import LongPage from "@/components/LongPage";
import mesHeroImg from "@/assets/products/mes/mes-hero-full.jpg.asset.json";

export default function MES() {
  const { t } = useLanguage();
  return (
    <LongPage
      breadcrumb={[{ name: "Products", to: "/#modules" }]}
      eyebrow={t("Manufacturing Execution")}
      title={t("Turn the plan into shop-floor reality.")}
      seoTitle="MES Software — Job Orders, Confirmations, QR Traceability — BEEDIGIT"
      seoDescription="Paperless job orders, real output and consumption confirmations, QR batch traceability — fully closed-loop with your ERP. Book a demo."
      description={t("BEEDIGIT MES gives operators a single tablet to run job orders, confirm output and consumption, and print QR labels — while your ERP gets clean, real-time facts back. No more paper, no more end-of-shift guesses, no more Monday reconciliations.")}
      bullets={[
        "Paperless job orders on the BEEPAD tablet",
        "Real output and consumption confirmed at the source",
        "QR traceability from raw input to finished batch",
        "Bi-directional ERP loop — live, not nightly",
      ]}
      schema="product"
      primaryCta={{ label: "Book a 30-min demo", to: "/get-started" }}
      secondaryCta={{ label: "Talk with expert", to: "/get-started" }}
ctaHeading="Stop running the factory on paper and memory."
      ctaBody="Show us one work center and one job order. In 30 minutes we'll show you how it runs paperless — with the ERP fully in the loop."
      heroVisual={
        <img
          src={mesHeroImg.url}
          alt="BEEDIGIT MES operator tablet showing job orders, confirmations and QR traceability"
          className="w-full h-auto"
          loading="eager"
        />
      }
      sections={[
        {
          kind: "personas",
          eyebrow: "Who it's for",
          heading: "If you live between ERP and the shop floor, this is for you",
          intro: "BEEDIGIT MES replaces the paper traveler, the WhatsApp message and the end-of-shift Excel with one workflow everyone trusts.",
          items: [
            { role: "COO / Ops Director", pain: "Plans look great in ERP and fall apart on the floor.", value: "ERP plan executed step by step, with confirmations flowing back live." },
            { role: "Plant Manager", pain: "Reconciling production vs plan eats half my week.", value: "Plan-vs-actual updated continuously — reconciliation is already done." },
            { role: "Production Manager", pain: "Operators write numbers on paper. I trust about half.", value: "Confirmations captured at the source, on the tablet, against the job order." },
            { role: "Supply Chain", pain: "I don't know what's really in WIP right now.", value: "Live WIP by job order and batch — no more end-of-day inventory surprises." },
            { role: "Quality / Compliance", pain: "Customer asks for traceability and we scramble.", value: "QR genealogy from raw drums to finished batch — pulled in one click." },
            { role: "CIO", pain: "Another disconnected system is the last thing I need.", value: "Sits on top of the ERP you already have — no rip-and-replace." },
          ],
        },
        {
          kind: "prose",
          eyebrow: "The problem",
          heading: "The plan leaves ERP. Then what?",
          body:
            "In most factories, the moment a job order is released, the ERP loses sight of it. A printed traveler walks to the line. Operators write down output on paper. A supervisor types it into Excel at the end of the shift — sometimes. The numbers reach the ERP a day later, already wrong.\n\nMeanwhile the floor has no idea if it's on pace, the planner is replanning blind, and the customer-service team can't say where the order is.\n\nBEEDIGIT MES closes that loop. The ERP releases the order, the operator runs it on a tablet, every confirmation lands back in the system in real time — and the whole business stops arguing about what was actually made.",
        },
        {
          kind: "steps",
          eyebrow: "How it works",
          heading: "Paperless execution in three steps",
          intro: "No ERP replacement, no master-data project. We sit between your plan and your floor — and connect the two for good.",
          items: [
            { n: 1, title: "We connect your ERP", body: "Job orders, BOM, routings and master data flow into BEEDIGIT through our standard REST/JSON adapters. No changes to your ERP." },
            { n: 2, title: "Operators run on a tablet", body: "BEEPAD shows the job order, the target speed, the BOM and the next checklist. Start, pause, scrap, scan, close — all by touch." },
            { n: 3, title: "ERP gets the truth back", body: "Output, consumption, downtime and batch genealogy stream back to your ERP and BI as they happen — no nightly batch, no reconciliation queue." },
          ],
        },
        {
          kind: "features",
          eyebrow: "What you get",
          heading: "Everything to execute a job order, day one",
          cols: 3,
          items: [
            { title: "Tablet job-order workflow", desc: "Start, pause, scrap, close — the operator never opens an ERP screen." },
            { title: "Output confirmations", desc: "Good and scrap quantities captured at the source, tied to the job order and machine." },
            { title: "Consumption confirmations", desc: "Raw material consumption against the BOM — reconciled with ERP automatically." },
            { title: "QR labels", desc: "Print SFG and FG labels from the line. Scan to trace, scan to consume." },
            { title: "Batch genealogy", desc: "Forward and backward traceability from raw drum to finished batch, in one query." },
            { title: "Plan vs actual, live", desc: "See how every order is tracking against plan — without exporting anything." },
            { title: "Operator checklists", desc: "Setup, in-process and end-of-run checks. Mandatory steps gate the next action." },
            { title: "Shift handover", desc: "One-page handover generated automatically. Nothing falls through the cracks." },
            { title: "Audit trail", desc: "Every action, every confirmation, every override — logged with user and timestamp." },
          ],
        },
        {
          kind: "callout",
          eyebrow: "Live in weeks",
          heading: "No 18-month MES project. No PLC overhaul.",
          body: "We stand up your first paperless work center in weeks, not quarters. Your ERP doesn't move, your operators get a tablet that actually makes their day easier, and the business gets clean confirmations from day one.",
          href: "/platform",
          linkLabel: "See the platform",
        },
        {
          kind: "gains",
          eyebrow: "What it's worth",
          heading: "What every role gets back",
          intro: "Each function gets a concrete, measurable win. Here's how it shows up.",
          items: [
            { role: "COO", cares: "Plan adherence across plants.", gain: "One execution standard, every site — the end of \"every plant runs differently\".", kpis: "Plan adherence, on-time-in-full, WIP turns.", pitch: "Same playbook in every factory." },
            { role: "Plant Manager", cares: "Hitting the schedule.", gain: "Live plan-vs-actual per order — adjust before the schedule slips, not after.", kpis: "Order completion %, schedule attainment, late starts.", pitch: "Catch slippage while you can still fix it." },
            { role: "Production Manager", cares: "Clean numbers from the floor.", gain: "Confirmations at the source — zero paper, zero retyping, zero end-of-shift Excel.", kpis: "Output accuracy, scrap %, confirmation latency.", pitch: "Trust the number on the screen." },
            { role: "Supply Chain", cares: "Knowing what's in WIP.", gain: "Live WIP by order, batch and stage — no more end-of-day surprises.", kpis: "WIP value, lead-time variance, stockouts.", pitch: "See WIP as it moves, not after." },
            { role: "Quality", cares: "Traceability under audit.", gain: "Full QR genealogy from raw drum to finished batch — recall scope in minutes, not days.", kpis: "Traceability cycle time, recall scope, audit findings.", pitch: "Pass any audit with one click." },
            { role: "CFO", cares: "Inventory accuracy and COGS.", gain: "Consumption confirmed against BOM live — inventory and COGS stop drifting.", kpis: "Inventory accuracy, BOM variance, COGS reconciliation effort.", pitch: "Numbers your auditor will actually believe." },
            { role: "CIO", cares: "Sustainable architecture.", gain: "ERP-safe overlay with open APIs — no fork of your system of record.", kpis: "Integration uptime, data completeness, change lead time.", pitch: "Add execution without breaking ERP." },
          ],
        },
        {
          kind: "useCases",
          eyebrow: "In the real world",
          heading: "What changes on Monday morning",
          items: [
            { persona: "The operator", scenario: "Scans the job-order QR, BEEPAD loads the BOM, target speed and first checklist.", outcome: "Starts the run in 30 seconds. No paper, no questions." },
            { persona: "The supervisor", scenario: "Sees Line 2 is behind plan at 11am, drills in, finds a slow cycle.", outcome: "Re-balances the shift before lunch — saves the day's target." },
            { persona: "The planner", scenario: "Releases a new order at 14:00. BEEPAD picks it up. By 14:05 the operator is running it.", outcome: "No traveler printed, no clipboard handed over." },
            { persona: "The QA lead", scenario: "Customer complaint references batch FG-23491. One scan opens the full genealogy.", outcome: "Recall scope identified in 90 seconds, not 3 days." },
            { persona: "The accountant", scenario: "Month-end. Consumption was confirmed live, against BOM, against actual orders.", outcome: "Inventory closes clean. No 3-day reconciliation marathon." },
            { persona: "The night shift", scenario: "Operator closes the shift on the tablet. Handover sheet is generated and waiting for the morning team.", outcome: "Day shift starts informed, not guessing." },
          ],
        },
        {
          kind: "mockups",
          eyebrow: "See it",
          heading: "Designed for the operator, trusted by the back office",
          intro: "One tablet on the line. One source of truth in the back office. Same data, zero retyping.",
          items: [
            { alt: "BEEPAD job-order screen", caption: "Job order, BOM, target speed and next action — on one screen the operator already understands." },
            { alt: "Output confirmation flow", caption: "Good and scrap captured in two taps. Tied to job, machine, operator and time." },
            { alt: "QR label and scan", caption: "Print and scan SFG/FG labels from the line. Genealogy writes itself." },
            { alt: "Plan vs actual dashboard", caption: "Every order, every line — green if on pace, red if slipping. Updated live." },
          ],
        },
        {
          kind: "kpi",
          eyebrow: "The numbers that matter",
          heading: "Four KPIs your operations team will live by",
          intro: "Live. Drillable to the order. Exportable to your ERP. No more end-of-shift Excel.",
          items: [
            { value: "Plan ↔ Actual", label: "Per order, updated live" },
            { value: "OTIF", label: "On-time-in-full, by order" },
            { value: "WIP", label: "Live by batch and stage" },
            { value: "Traceability", label: "Minutes, not days" },
          ],
        },
        {
          kind: "split",
          eyebrow: "Why BEEDIGIT MES",
          heading: "What you have today vs what you'll have next month",
          leftTitle: "Today",
          rightTitle: "With BEEDIGIT MES",
          left: [
            "Paper travelers and printed BOMs",
            "Operators retype numbers into Excel at end of shift",
            "ERP confirmations land a day late, already wrong",
            "Traceability means \"give us three days\"",
            "Plan vs actual is a Monday morning argument",
            "Inventory drifts; month-end is a reconciliation marathon",
          ],
          right: [
            "Tablet job orders, paperless from day one",
            "Confirmations captured at the source, in real time",
            "ERP gets clean facts as they happen",
            "One QR scan opens full batch genealogy",
            "Plan vs actual is live on every screen",
            "Inventory and COGS reconcile themselves",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Hardware included",
          heading: "We bring the kit. You keep producing.",
          intro: "Operator tablets, label printers and connectivity — provisioned, mounted and ready on day one.",
          items: [
            "BEEPAD operator tablet",
            "BEEDIGIT HUB",
            "Label printer kit",
            "Handheld QR scanner",
            "GSM · Wi-Fi · Ethernet",
            "MQTT over TLS",
          ],
        },
        {
          kind: "chips",
          eyebrow: "Plays well with your stack",
          heading: "Your ERP, your BI, your way",
          intro: "Bi-directional with the tools you already run on — no integration project.",
          items: [
            "factory.online", "SAP", "Oracle EBS / Fusion", "Microsoft Dynamics", "Odoo",
            "Power BI", "Tableau", "Data lakes (CSV / JSON / REST)",
            "MQTT", "OPC UA", "REST / Webhooks",
          ],
        },
        {
          kind: "split",
          eyebrow: "Your rollout",
          heading: "From paper to paperless in 90 days",
          leftTitle: "Weeks 1–4",
          rightTitle: "Weeks 5–12",
          left: [
            "Discovery: pick the first 3 work centers and one product family",
            "ERP adapter wired in — orders flowing into BEEDIGIT",
            "Tablets mounted, operators trained in under an hour",
            "First paperless shift — and your first \"we should have done this years ago\" moment",
          ],
          right: [
            "Roll out remaining work centers and product families",
            "QR labels and genealogy enabled end-to-end",
            "Confirmations flowing back to ERP at full volume",
            "Paper travelers retired — for good",
          ],
        },
        {
          kind: "callout",
          eyebrow: "Officially certified",
          heading: "The only MES certified by INCIT for raising your SIRI index",
          body: "Industry 4.0 grant programs, government incentives, sustainability reports — BEEDIGIT counts. We're the first platform officially recognized for moving the needle on the Smart Industry Readiness Index.",
          href: "/siri",
          linkLabel: "See how BEEDIGIT raises SIRI",
        },
        {
          kind: "faq",
          eyebrow: "FAQ",
          heading: "What buyers ask us first",
          items: [
            { q: "Do we have to replace our ERP?", a: "No — BEEDIGIT MES sits between your ERP and the floor. ERP stays your system of record; we handle execution and close the loop with clean confirmations." },
            { q: "How long until the first work center is paperless?", a: "Typically 2–4 weeks from kickoff to the first fully paperless shift on the first work center. Full plant rollout follows in 60–90 days." },
            { q: "Will operators actually use a tablet?", a: "Yes. BEEPAD is closer to a phone app than a SCADA screen. Training is under an hour, and adoption is consistently above 90% in the first month." },
            { q: "What if connectivity drops?", a: "Devices buffer 24 hours locally over encrypted MQTT/TLS. If the network blinks, you lose nothing — confirmations sync the moment it's back." },
            { q: "How does traceability work in practice?", a: "Every batch — raw, SFG, FG — gets a QR. Scans during consumption and packing build the genealogy automatically. One scan = full forward/backward trace." },
            { q: "Will it work with our existing labels and printers?", a: "Usually yes — we support standard Zebra/Honeywell printers and most label formats. If you have a non-standard setup, we'll validate during discovery." },
            { q: "What about quality and maintenance?", a: "Same data model powers Quality and CMMS modules. When you're ready, you turn them on — no second integration, no second source of truth." },
            { q: "What does it cost?", a: "Priced per connected work center, hardware included. Most customers see payback inside two quarters from reconciliation time alone. Book the demo — we'll build your business case." },
          ],
        },
        {
          kind: "related",
          eyebrow: "Explore the platform",
          heading: "Pairs naturally with",
          items: [
            { title: "Monitoring & OEE", href: "/products/monitoring-oee", desc: "Live OEE per machine — find the lost hours behind the missed plan." },
            { title: "Quality Management", href: "/products/quality-management", desc: "Digital checklists and batch traceability at the point of work." },
            { title: "CMMS", href: "/products/cmms", desc: "Tie maintenance directly to the downtime that hurts production." },
            { title: "Workspace", href: "/products/workspace", desc: "Andon, issues and handover — the coordination layer above execution." },
          ],
        },
      ]}
    />
  );
}